import classnames from 'classnames';
import { debounce } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { RicosContext } from 'ricos-context';
import Tooltip from 'wix-rich-content-common/libs/Tooltip';
import { s as styleInject } from '../style-inject.es-746bb8ed.js';
import { ROOT, FILE_UPLOAD_NAME_CONTAINER, FILE_UPLOAD_NAME, FILE_UPLOAD_EXTENSION } from '../dataHooks.js';
import { FILE_UPLOAD_TYPE } from '../types.js';
import { getIcon } from '../icons/getIcon.js';
import ErrorIcon from '../icons/errorIcon.js';
import LoaderIcon from '../icons/LoaderIcon.js';
import ReadyIcon from '../icons/readyIcon.js';
import DownloadIcon from '../icons/downloadIcon.js';
import '../lib/fileExtensionToType.js';
import '../icons/docIcon.js';
import '../icons/folderIcon.js';
import '../icons/imageIcon.js';
import '../icons/otherIcon.js';
import '../icons/pdfIcon.js';
import '../icons/pptIcon.js';
import '../icons/vid-aud-icon.js';
import '../icons/xlsIcon.js';

var css_248z = ".bQvEk,.glob_fontElementMap{font-family:var(--ricos-font-family,unset)}.niC75{box-sizing:border-box;font-family:var(--ricos-custom-p-font-family);height:84px;overflow:hidden;position:relative;width:100%}[dir] .niC75{border:1px solid rgba(var(--ricos-text-color-tuple,33,33,33),.2)}[dir] .niC75:hover{border:1px solid rgba(var(--ricos-text-color-tuple,33,33,33),.6);cursor:pointer}@supports not (contain:inline-size){@media only screen and (max-width:640px){.niC75{color:var(--ricos-native-text-color,var(--ricos-text-color,#212121));font-family:var(--ricos-native-text-font-family,var(--ricos-custom-p-font-family,unset))}[dir] .niC75{border-radius:var(--ricos-native-whitebox-border-radius,0)}}}@container (width < 640px){.niC75{color:var(--ricos-native-text-color,var(--ricos-text-color,#212121));font-family:var(--ricos-native-text-font-family,var(--ricos-custom-p-font-family,unset))}[dir] .niC75{border-radius:var(--ricos-native-whitebox-border-radius,0)}}[dir] .BL3CM{border:1px solid rgba(246,77,67,.8)}[dir] .BL3CM:hover{border:1px solid #f64d43}.YtroN{align-content:flex-start;box-sizing:border-box;display:flex;flex-flow:column wrap;height:100%;justify-content:center;text-decoration:none;width:100%}.YtroN.wU7fx{align-content:center}.Q-0uZ{display:flex;flex-flow:column nowrap;position:absolute;width:60%}[dir=ltr] .Q-0uZ{left:73px}[dir=rtl] .Q-0uZ{right:73px}[dir=ltr] .Q-0uZ.CCdgZ{left:62px}[dir=rtl] .Q-0uZ.CCdgZ{right:62px}.LK-ek{color:var(--ricos-text-color,#212121);position:absolute}[dir] .LK-ek{margin-top:2px}[dir=ltr] .LK-ek{margin-left:32px;right:32px}[dir=rtl] .LK-ek{left:32px;margin-right:32px}.UBj3W{color:var(--ricos-text-color,#212121);height:100%;width:40px}[dir=ltr] .UBj3W{margin:32px 12px 28px 20px}[dir=rtl] .UBj3W{margin:32px 20px 28px 12px}.QOmKR{display:flex;flex-flow:row nowrap}._2C0qE{color:var(--ricos-text-color,#212121);font-size:16px;height:24px;max-width:80%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}[dir=ltr] ._2C0qE{text-align:left}[dir=rtl] ._2C0qE{text-align:right}.dDzda{color:var(--ricos-text-color,#212121);font-size:16px;max-width:20%;white-space:nowrap}[dir=ltr] .dDzda{margin-right:30px;text-align:left}[dir=rtl] .dDzda{margin-left:30px;text-align:right}[dir=ltr] .dDzda.hfGVc{margin-right:55px}[dir=rtl] .dDzda.hfGVc{margin-left:55px}.BIxOf{color:var(--ricos-action-color,#116dff)}[dir=ltr] .BIxOf{margin-left:20px;margin-right:12px}[dir=rtl] .BIxOf{margin-left:12px;margin-right:20px}[dir] .BIxOf.wU7fx{margin:0}.ADbip{animation-duration:1.5s;animation-iteration-count:infinite;animation-name:J-AdI;animation-timing-function:linear}.hIc7R{color:var(--ricos-text-color,#212121);font-size:14px;letter-spacing:0;line-height:20px;opacity:.6;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}[dir] .hIc7R{text-align:initial}.VzBqh{color:#f64d43;font-size:14px;letter-spacing:0}.RAGT5{stroke:var(--ricos-background-color,#fff)}.ajfn9{fill:var(--ricos-background-color,#fff)}@keyframes J-AdI{0%{transform:rotate(0deg)}to{transform:rotate(1turn)}}";
var styles = {"fontElementMap":"bQvEk","file_upload_container":"niC75","file_upload_error_container":"BL3CM","file_upload_link":"YtroN","width_three":"wU7fx","file_upload_text_container":"Q-0uZ","width_two":"CCdgZ","file_upload_state":"LK-ek","mobile_status_icon":"UBj3W","file_upload_name_container":"QOmKR","file_upload_name":"_2C0qE","file_upload_extension":"dDzda","width_one":"hfGVc","file_upload_type_icon":"BIxOf","file_loader_icon":"ADbip","spin":"J-AdI","file_upload_type":"hIc7R","file_upload_text_error":"VzBqh","file_upload_icon_frame":"RAGT5","file_upload_icon_background":"ajfn9"};
styleInject(css_248z);

const getNameWithoutType = (fileName) => {
  if (!fileName || !fileName.includes(".")) {
    return fileName;
  }
  const s = fileName.split(".");
  return s.slice(0, s.length - 1).join(".");
};
const resizeWidths = { first: 320, second: 140, third: 100 };
const FileViewer = ({ settings, componentData, isLoading = false, tempDataPlaceHolder, helpers }) => {
  var _a;
  const { isMobile, t } = useContext(RicosContext);
  const downloaderRef = React.useRef(null);
  const [fileUploadViewerRef, setFileUploadViewerRef] = React.useState(null);
  const [resolvedFileUrl, setResolvedFileUrl] = React.useState(null);
  const [resolvingUrl, setResolvingUrl] = React.useState(false);
  const [showReadyIcon, setShowReadyIcon] = React.useState(false);
  const [currentWidth, setCurrentWidth] = React.useState(0);
  useEffect(() => {
    if (!fileUploadViewerRef || !(window == null ? void 0 : window.ResizeObserver)) {
      return;
    }
    const resizer = new ResizeObserver(
      debounce((entries) => {
        setCurrentWidth(Math.round(entries[0].contentRect.width));
      }, 60)
    );
    resizer.observe(fileUploadViewerRef);
    return () => {
      resizer.unobserve(fileUploadViewerRef);
    };
  }, [fileUploadViewerRef]);
  useEffect(() => {
    if (isLoading) {
      switchReadyIcon();
    }
  }, [isLoading]);
  const switchReadyIcon = () => {
    setShowReadyIcon(true);
    setTimeout(() => setShowReadyIcon(false), 2e3);
  };
  const isInResizeRange = (resizeWidth) => (fileUploadViewerRef == null ? void 0 : fileUploadViewerRef.clientWidth) && fileUploadViewerRef.clientWidth < resizeWidth;
  const renderContainerWithoutLink = () => {
    const { name, type } = tempDataPlaceHolder ? tempDataPlaceHolder : componentData;
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classnames(styles.file_upload_link, {
          [styles.width_three]: isInResizeRange(resizeWidths.third)
        })
      },
      renderViewerBody({ name, type })
    );
  };
  const renderIcon = (Icon) => {
    const showLoader = isLoading || resolvingUrl;
    const showFileIcon = !showLoader && !showReadyIcon && isMobile || !isMobile && Icon;
    if (showFileIcon) {
      return /* @__PURE__ */ React.createElement(
        Icon,
        {
          styles,
          className: classnames(styles.file_upload_type_icon, {
            [styles.width_three]: isInResizeRange(resizeWidths.third)
          })
        }
      );
    } else {
      return /* @__PURE__ */ React.createElement("div", { className: isMobile ? styles.mobile_status_icon : styles.file_upload_state }, componentData.error ? /* @__PURE__ */ React.createElement(ErrorIcon, null) : showLoader ? /* @__PURE__ */ React.createElement(LoaderIcon, { className: styles.file_loader_icon }) : showReadyIcon ? /* @__PURE__ */ React.createElement(ReadyIcon, null) : !isInResizeRange(resizeWidths.first) && /* @__PURE__ */ React.createElement(DownloadIcon, null));
    }
  };
  const sizeToString = (size) => {
    return size < 1e3 ? size + "B" : size < 1e6 ? Math.round(size / 1e3) + "KB" : (size / 1e6).toFixed(2) + "MB";
  };
  const getFileInfoString = (type) => {
    if (componentData.error) {
      return {
        infoString: t("UploadFile_Error_Generic_Item"),
        infoStyle: styles.file_upload_text_error
      };
    }
    const translationKey = isLoading || resolvingUrl ? "UploadFile_Viewer_Loader" : "UploadFile_Viewer_Download";
    const size = componentData.size || (tempDataPlaceHolder == null ? void 0 : tempDataPlaceHolder.size);
    if (size) {
      return {
        infoString: t(translationKey, {
          fileType: type == null ? void 0 : type.toUpperCase()
        }) + " \u2022 " + sizeToString(size),
        infoStyle: styles.file_upload_type
      };
    }
    return {
      infoString: t(translationKey, {
        fileType: type == null ? void 0 : type.toUpperCase()
      }),
      infoStyle: styles.file_upload_type
    };
  };
  const renderViewerBody = ({ name, type }) => {
    const nameWithoutType = getNameWithoutType(name);
    const Icon = getIcon(type);
    const { infoString, infoStyle } = getFileInfoString(type);
    return /* @__PURE__ */ React.createElement(React.Fragment, null, renderIcon(Icon), !isMobile && renderIcon(), !isInResizeRange(resizeWidths.third) && /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classnames(styles.file_upload_text_container, {
          [styles.width_two]: isInResizeRange(resizeWidths.second)
        })
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: styles.file_upload_name_container,
          "data-hook": FILE_UPLOAD_NAME_CONTAINER
        },
        !isInResizeRange(resizeWidths.second) && /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_name, "data-hook": FILE_UPLOAD_NAME }, nameWithoutType),
        type && /* @__PURE__ */ React.createElement(
          "div",
          {
            className: classnames(styles.file_upload_extension, {
              [styles.width_one]: isInResizeRange(resizeWidths.first)
            }),
            "data-hook": FILE_UPLOAD_EXTENSION
          },
          "." + type
        )
      ),
      /* @__PURE__ */ React.createElement("div", { className: infoStyle }, infoString)
    ));
  };
  const renderViewer = (fileUrl2) => {
    const { name, type, error } = componentData;
    const { downloadTarget } = settings;
    if (error || tempDataPlaceHolder) {
      return renderContainerWithoutLink();
    }
    return /* @__PURE__ */ React.createElement(
      "a",
      {
        "data-cancel-link": true,
        href: fileUrl2,
        target: downloadTarget,
        className: classnames(styles.file_upload_link, {
          [styles.width_three]: isInResizeRange(resizeWidths.third)
        }),
        onClick: onFileClick
      },
      renderViewerBody({ name, type })
    );
  };
  const renderFileUrlResolver = () => {
    const { name, type, error } = componentData;
    if (error) {
      return renderContainerWithoutLink();
    }
    const fileUrlResolver = () => {
      var _a2, _b;
      if (!resolvingUrl) {
        setResolvingUrl(true);
        settings.resolveFileUrl({
          id: (_a2 = componentData == null ? void 0 : componentData.src) == null ? void 0 : _a2.id,
          type: componentData.type,
          path: componentData.path,
          mimeType: componentData.mimeType,
          name: componentData.name,
          size: componentData.size,
          pdfSettings: componentData.pdfSettings,
          privacy: ((_b = componentData == null ? void 0 : componentData.src) == null ? void 0 : _b.private) ? "private" : "public"
        }).then((resolvedFileUrl2) => {
          setResolvedFileUrl(resolvedFileUrl2);
          setResolvingUrl(false);
          switchReadyIcon();
          if (downloaderRef.current) {
            downloaderRef.current.href = resolvedFileUrl2;
            downloaderRef.current.download = name;
            downloaderRef.current.click();
          }
        });
      }
    };
    const resolveIfEnter = (ev) => {
      const enterEvent = 13;
      if (ev.which === enterEvent) {
        fileUrlResolver();
      }
    };
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        onClick: fileUrlResolver,
        onKeyDown: resolveIfEnter,
        role: "button",
        tabIndex: 0,
        className: classnames(styles.file_upload_link, {
          [styles.width_three]: isInResizeRange(resizeWidths.third)
        })
      },
      renderViewerBody({ name, type })
    );
  };
  const renderAutoDownloadLinkRef = () => {
    const { resolveFileUrl: withFileUrlResolver, downloadTarget } = settings;
    if (!withFileUrlResolver) {
      return null;
    }
    const target = downloadTarget || "_blank";
    return /* @__PURE__ */ React.createElement(
      "a",
      {
        "data-cancel-link": true,
        href: "#0",
        ref: (ref) => downloaderRef.current = ref,
        style: { display: "none" },
        target
      },
      /* @__PURE__ */ React.createElement(React.Fragment, null)
    );
  };
  const onFileClick = () => {
    var _a2;
    return (
      // @ts-ignore
      (_a2 = helpers == null ? void 0 : helpers.onViewerAction) == null ? void 0 : _a2.call(helpers, FILE_UPLOAD_TYPE, "Click")
    );
  };
  const fileUrl = ((_a = componentData.src) == null ? void 0 : _a.url) || resolvedFileUrl;
  const viewer = fileUrl || tempDataPlaceHolder ? renderViewer(fileUrl) : renderFileUrlResolver();
  return /* @__PURE__ */ React.createElement(
    Tooltip,
    {
      content: isInResizeRange(resizeWidths.first) && componentData.name,
      tooltipOffset: { y: 25 }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        "data-hook": ROOT,
        ref: setFileUploadViewerRef,
        className: classnames(
          styles.file_upload_container,
          componentData.error && styles.file_upload_error_container
        )
      },
      viewer,
      renderAutoDownloadLinkRef()
    )
  );
};

export { FileViewer };
//# sourceMappingURL=file-viewer.js.map
